/**
 * Conversation list — slide-out sidebar showing chat history.
 */

import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { PlusIcon, TrashIcon, CloseIcon } from '@components/icons';

export function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const ConversationList = observer(function ConversationList() {
  const conversations = bibleState.chatConversations;
  const activeId = bibleState.activeConversationId;
  const isLoaded = bibleState.isConversationsLoaded;

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <span className="text-sm font-semibold text-stone-700 dark:text-stone-300">
          Conversations
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => bibleState.startNewChatConversation()}
            className="conversation-list-btn"
            title="New conversation"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => bibleState.hideConversationList()}
            className="conversation-list-btn"
            title="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="conversation-list-items">
        {!isLoaded && (
          <p className="px-3 py-4 text-center text-sm text-stone-400">Loading...</p>
        )}

        {isLoaded && conversations.length === 0 && (
          <p className="px-3 py-4 text-center text-sm text-stone-400">
            No conversations yet
          </p>
        )}

        {conversations.map((conv) => (
          <div
            key={conv.id}
            className={`conversation-item ${conv.id === activeId ? 'conversation-item-active' : ''}`}
            onClick={() => bibleState.selectChatConversation(conv.id)}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-stone-800 dark:text-stone-200">
                {conv.title}
              </p>
              <p className="truncate text-xs text-stone-400 dark:text-stone-500">
                {relativeTime(conv.updatedAt)}
                {conv.messageCount > 0 && ` \u00B7 ${conv.messageCount} msgs`}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                bibleState.deleteChatConversation(conv.id);
              }}
              className="ml-1 flex-shrink-0 rounded p-1 text-stone-300 transition-colors hover:text-red-500 dark:text-stone-600 dark:hover:text-red-400"
              title="Delete conversation"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
});
